import { useQuery } from "@tanstack/react-query";
import { AllowListABI } from "@turnstile-portal/l1-artifacts-abi";
import type { Hex } from "@turnstile-portal/turnstile.js";
import type { PublicClient } from "viem";
import { erc20Abi, getContract, isAddress } from "viem";
import { usePublicClient } from "wagmi";
import { getL1AllowListAddr } from "../lib/addresses";
import { getQueryClient } from "../lib/queryClient";
import { getL1ClientAsync } from "../wagmi";

const erc20TokenInfoQueryKey = (tokenAddress: Hex) => ["erc20TokenInfo", tokenAddress];
const isValidEthTokenAddrQueryKey = (tokenAddress: Hex) => ["isValidEthTokenAddr", tokenAddress];
const isApproverQueryKey = (address: Hex) => ["isApprover", address];

// Used as fallback when token info is not yet in API cache
export async function fetchErc20TokenInfo(tokenAddress: Hex, publicClient: PublicClient) {
  const contract = getContract({
    address: tokenAddress,
    abi: erc20Abi,
    client: publicClient,
  });

  const [name, symbol, decimals] = await Promise.all([
    contract.read.name(),
    contract.read.symbol(),
    contract.read.decimals(),
  ]);

  console.debug(`Fetched ERC20 token info for ${tokenAddress}`, { name, symbol, decimals });
  return { name, symbol, decimals, l1Address: tokenAddress };
}

export async function getErc20TokenInfo(tokenAddress: Hex) {
  const l1Client = await getL1ClientAsync();
  const queryClient = getQueryClient();
  return queryClient.fetchQuery({
    queryKey: erc20TokenInfoQueryKey(tokenAddress),
    queryFn: () => fetchErc20TokenInfo(tokenAddress, l1Client.getPublicClient()),
    staleTime: Infinity, // "static" equivalent - never stale
  });
}

// Internal helper function for token validation
async function isValidERC20Token(tokenAddress: Hex, publicClient: PublicClient): Promise<boolean> {
  try {
    const { name, symbol, decimals } = await fetchErc20TokenInfo(tokenAddress, publicClient);

    // If all calls succeed, it's likely a valid ERC20 token
    console.log({ name, symbol, decimals });
    return true;
  } catch (_error) {
    return false;
  }
}

export function useIsValidEthTokenAddr(tokenAddress: Hex) {
  const publicClient = usePublicClient();
  const { data: isValid } = useQuery({
    queryKey: isValidEthTokenAddrQueryKey(tokenAddress),
    queryFn: async () => {
      if (!isAddress(tokenAddress)) {
        return false;
      }
      return await isValidERC20Token(tokenAddress, publicClient);
    },
  });

  return isValid;
}

export async function fetchIsApprover(address: Hex, publicClient: PublicClient): Promise<boolean> {
  console.debug("fetchIsApprover called for", address);
  const isApprover = await publicClient.readContract({
    address: getL1AllowListAddr(),
    abi: AllowListABI,
    functionName: "isApprover",
    args: [address],
  });
  console.debug("fetchIsApprover result:", isApprover);
  return isApprover;
}

export function useIsApproverQuery(address: Hex | undefined) {
  const publicClient = usePublicClient();
  return useQuery({
    queryKey: isApproverQueryKey(address || "0x0000000000000000000000000000000000000000"),
    queryFn: () => {
      if (!address) return Promise.resolve(false);
      return fetchIsApprover(address, publicClient);
    },
    enabled: !!address,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
